import React, { useCallback, useEffect, useRef, useState } from 'react';
import { io, Socket } from 'socket.io-client';
import { getLogs, LogEntry } from '../api/logs';
import { useTheme } from '../contexts/ThemeContext';

interface LogsPageProps {
  onClose: () => void;
  embedded?: boolean;
}

const LEVEL_COLORS: Record<string, string> = {
  error: 'text-red-400',
  warn: 'text-amber-400',
  info: 'text-cyan-400',
  debug: 'text-slate-400',
  verbose: 'text-purple-400',
};

const LEVEL_BG: Record<string, string> = {
  error: 'bg-red-500/20 text-red-400 border-red-500/30',
  warn: 'bg-amber-500/20 text-amber-400 border-amber-500/30',
  info: 'bg-cyan-500/20 text-cyan-400 border-cyan-500/30',
  debug: 'bg-slate-500/20 text-slate-400 border-slate-500/30',
  verbose: 'bg-purple-500/20 text-purple-400 border-purple-500/30',
};

const LogsPage: React.FC<LogsPageProps> = ({ onClose, embedded = false }) => {
  const { theme } = useTheme();
  const isDark = theme === 'dark';
  const [logs, setLogs] = useState<LogEntry[]>([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('all');
  const [search, setSearch] = useState('');
  const [autoScroll, setAutoScroll] = useState(true);
  const [paused, setPaused] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);
  const socketRef = useRef<Socket | null>(null);

  const loadLogs = useCallback(async () => {
    try {
      setLoading(true);
      const fetched = await getLogs(500);
      setLogs(fetched);
    } catch (err) {
      console.error('Failed to load logs:', err);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadLogs();
  }, [loadLogs]);

  useEffect(() => {
    // websocket-only: HTTP long-polling would need sticky sessions when running multiple replicas
    const socket = io({ path: '/ws', transports: ['websocket'] });
    socketRef.current = socket;

    socket.on('logEntry', (entry: LogEntry) => {
      if (!paused) {
        setLogs((prev) => {
          const next = [...prev, entry];
          if (next.length > 1000) return next.slice(-1000);
          return next;
        });
      }
    });

    return () => {
      socket.disconnect();
    };
  }, [paused]);

  useEffect(() => {
    if (autoScroll && containerRef.current) {
      containerRef.current.scrollTop = containerRef.current.scrollHeight;
    }
  }, [logs, autoScroll]);

  const handleScroll = () => {
    if (!containerRef.current) return;
    const { scrollTop, scrollHeight, clientHeight } = containerRef.current;
    const atBottom = scrollHeight - scrollTop - clientHeight < 50;
    setAutoScroll(atBottom);
  };

  const filteredLogs = logs.filter((log) => {
    if (filter !== 'all' && log.level !== filter) return false;
    if (search) {
      const q = search.toLowerCase();
      return log.message.toLowerCase().includes(q) || (log.service || '').toLowerCase().includes(q);
    }
    return true;
  });

  const formatTime = (ts: string) => {
    try {
      const d = new Date(ts);
      return d.toLocaleString('en-US', {
        hour12: false,
        month: 'short',
        day: '2-digit',
        hour: '2-digit',
        minute: '2-digit',
        second: '2-digit',
      });
    } catch {
      return ts;
    }
  };

  const overlay = isDark ? 'bg-slate-900/95' : 'bg-white/95';
  const cardBg = isDark ? 'bg-slate-900' : 'bg-white';
  const borderCol = isDark ? 'border-slate-700' : 'border-slate-200';
  const logBg = isDark ? 'bg-slate-950' : 'bg-slate-50';
  const toolbarBg = isDark ? 'bg-slate-800' : 'bg-slate-100';

  const btnClass = isDark
    ? 'px-2.5 py-1 text-xs font-medium rounded transition-colors text-slate-300 hover:text-white hover:bg-white/10'
    : 'px-2.5 py-1 text-xs font-medium rounded transition-colors text-slate-600 hover:text-slate-900 hover:bg-slate-200';

  const btnActiveClass = isDark
    ? 'px-2.5 py-1 text-xs font-medium rounded transition-colors bg-white/15 text-white'
    : 'px-2.5 py-1 text-xs font-medium rounded transition-colors bg-slate-300 text-slate-900';

  if (embedded) {
    return (
      <div className="flex-1 flex flex-col overflow-hidden" style={{ backgroundColor: 'var(--surface)' }}>
        {/* Header */}
        <div className={`flex items-center justify-between px-4 py-3 border-b ${borderCol}`}>
          <div className="flex items-center gap-3">
            <h2 className="text-sm font-bold tracking-tight" style={{ color: 'var(--text-primary)' }}>
              Application Logs
            </h2>
            <span
              className="text-[10px] font-mono px-1.5 py-0.5 rounded"
              style={{ color: 'var(--text-muted)', background: 'var(--surface-overlay)' }}
            >
              {filteredLogs.length} entries
            </span>
            {!autoScroll && (
              <button
                onClick={() => {
                  setAutoScroll(true);
                  if (containerRef.current) containerRef.current.scrollTop = containerRef.current.scrollHeight;
                }}
                className="text-[10px] font-medium px-2 py-0.5 rounded bg-cyan-500/20 text-cyan-400 hover:bg-cyan-500/30"
              >
                ↓ Jump to bottom
              </button>
            )}
          </div>
        </div>

        {/* Toolbar */}
        <div className={`flex items-center gap-2 px-4 py-2 border-b ${borderCol} ${toolbarBg}`}>
          {['all', 'error', 'warn', 'info', 'debug'].map((level) => (
            <button key={level} onClick={() => setFilter(level)} className={filter === level ? btnActiveClass : btnClass}>
              {level === 'all' ? 'All' : level.charAt(0).toUpperCase() + level.slice(1)}
            </button>
          ))}
          <div className="w-px h-4 mx-1" style={{ background: 'var(--border)' }} />
          <input
            type="text"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search logs..."
            className="border text-xs rounded-md px-2.5 py-1.5 focus:outline-none focus:ring-1 focus:ring-cyan-500/40 w-48 font-mono"
            style={{ backgroundColor: 'var(--input-bg)', borderColor: 'var(--input-border)', color: 'var(--text-primary)' }}
          />
          <div className="flex-1" />
          <button onClick={() => setPaused(!paused)} className={paused ? btnActiveClass : btnClass}>
            {paused ? '▶ Resume' : '⏸ Pause'}
          </button>
          <button onClick={loadLogs} className={btnClass}>
            ↻ Refresh
          </button>
          <button
            onClick={() => {
              const content = filteredLogs
                .map((l) => `${l.timestamp} [${l.level.toUpperCase()}] ${l.service || '-'}: ${l.message}${l.stack ? '\n' + l.stack : ''}`)
                .join('\n');
              const blob = new Blob([content], { type: 'text/plain' });
              const url = URL.createObjectURL(blob);
              const a = document.createElement('a');
              a.href = url;
              a.download = `optialert-logs-${new Date().toISOString().slice(0, 19).replace(/:/g, '-')}.txt`;
              a.click();
              URL.revokeObjectURL(url);
            }}
            className={btnClass}
          >
            ↓ Download
          </button>
          <button onClick={() => setLogs([])} className={btnClass}>
            Clear
          </button>
        </div>

        {/* Log output */}
        <div ref={containerRef} onScroll={handleScroll} className={`flex-1 overflow-auto font-mono text-xs leading-relaxed ${logBg}`}>
          {loading ? (
            <div className="flex items-center justify-center h-32">
              <span className="text-xs" style={{ color: 'var(--text-muted)' }}>
                Loading logs...
              </span>
            </div>
          ) : filteredLogs.length === 0 ? (
            <div className="flex items-center justify-center h-32">
              <span className="text-xs" style={{ color: 'var(--text-muted)' }}>
                No logs to display
              </span>
            </div>
          ) : (
            <table className="w-full">
              <tbody>
                {filteredLogs.map((log, i) => (
                  <tr key={i} className={`border-b ${isDark ? 'border-slate-800 hover:bg-slate-800/50' : 'border-slate-100 hover:bg-slate-100'}`}>
                    <td className="px-3 py-1 whitespace-nowrap align-top" style={{ color: 'var(--text-muted)' }}>
                      {formatTime(log.timestamp)}
                    </td>
                    <td className="px-2 py-1 whitespace-nowrap align-top">
                      <span className={`inline-block text-[10px] font-bold px-1.5 py-0.5 rounded border ${LEVEL_BG[log.level] || LEVEL_BG.info}`}>
                        {log.level.toUpperCase()}
                      </span>
                    </td>
                    <td className="px-2 py-1 whitespace-nowrap align-top" style={{ color: 'var(--text-muted)' }}>
                      {log.service || '-'}
                    </td>
                    <td className={`px-3 py-1 align-top break-all ${LEVEL_COLORS[log.level] || 'text-slate-300'}`}>
                      {log.message}
                      {log.stack && <pre className="mt-1 text-[10px] text-red-400/70 whitespace-pre-wrap">{log.stack as string}</pre>}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      </div>
    );
  }

  return (
    <div className={`fixed inset-0 z-50 flex items-center justify-center ${overlay} backdrop-blur-sm`}>
      <div className={`w-full h-full max-w-7xl max-h-[95vh] m-4 ${cardBg} rounded-xl border ${borderCol} shadow-2xl flex flex-col overflow-hidden`}>
        {/* Header */}
        <div className={`flex items-center justify-between px-4 py-3 border-b ${borderCol}`}>
          <div className="flex items-center gap-3">
            <h2 className="text-sm font-bold tracking-tight" style={{ color: 'var(--text-primary)' }}>
              Application Logs
            </h2>
            <span
              className="text-[10px] font-mono px-1.5 py-0.5 rounded"
              style={{ color: 'var(--text-muted)', background: 'var(--surface-overlay)' }}
            >
              {filteredLogs.length} entries
            </span>
            {!autoScroll && (
              <button
                onClick={() => {
                  setAutoScroll(true);
                  if (containerRef.current) containerRef.current.scrollTop = containerRef.current.scrollHeight;
                }}
                className="text-[10px] font-medium px-2 py-0.5 rounded bg-cyan-500/20 text-cyan-400 hover:bg-cyan-500/30"
              >
                ↓ Jump to bottom
              </button>
            )}
          </div>
          <button onClick={onClose} className={btnClass}>
            ✕ Close
          </button>
        </div>

        {/* Toolbar */}
        <div className={`flex items-center gap-2 px-4 py-2 border-b ${borderCol} ${toolbarBg}`}>
          {/* Level filter */}
          {['all', 'error', 'warn', 'info', 'debug'].map((level) => (
            <button key={level} onClick={() => setFilter(level)} className={filter === level ? btnActiveClass : btnClass}>
              {level === 'all' ? 'All' : level.charAt(0).toUpperCase() + level.slice(1)}
            </button>
          ))}

          <div className="w-px h-4 mx-1" style={{ background: 'var(--border)' }} />

          {/* Search */}
          <input
            type="text"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search logs..."
            className="border text-xs rounded-md px-2.5 py-1.5 focus:outline-none focus:ring-1 focus:ring-cyan-500/40 w-48 font-mono"
            style={{ backgroundColor: 'var(--input-bg)', borderColor: 'var(--input-border)', color: 'var(--text-primary)' }}
          />

          <div className="flex-1" />

          {/* Pause / Resume */}
          <button onClick={() => setPaused(!paused)} className={paused ? btnActiveClass : btnClass}>
            {paused ? '▶ Resume' : '⏸ Pause'}
          </button>

          {/* Refresh */}
          <button onClick={loadLogs} className={btnClass}>
            ↻ Refresh
          </button>

          {/* Download */}
          <button
            onClick={() => {
              const content = filteredLogs
                .map((l) => `${l.timestamp} [${l.level.toUpperCase()}] ${l.service || '-'}: ${l.message}${l.stack ? '\n' + l.stack : ''}`)
                .join('\n');
              const blob = new Blob([content], { type: 'text/plain' });
              const url = URL.createObjectURL(blob);
              const a = document.createElement('a');
              a.href = url;
              a.download = `optialert-logs-${new Date().toISOString().slice(0, 19).replace(/:/g, '-')}.txt`;
              a.click();
              URL.revokeObjectURL(url);
            }}
            className={btnClass}
          >
            ↓ Download
          </button>

          {/* Clear */}
          <button onClick={() => setLogs([])} className={btnClass}>
            Clear
          </button>
        </div>

        {/* Log output */}
        <div ref={containerRef} onScroll={handleScroll} className={`flex-1 overflow-auto font-mono text-xs leading-relaxed ${logBg}`}>
          {loading ? (
            <div className="flex items-center justify-center h-32">
              <span className="text-xs" style={{ color: 'var(--text-muted)' }}>
                Loading logs...
              </span>
            </div>
          ) : filteredLogs.length === 0 ? (
            <div className="flex items-center justify-center h-32">
              <span className="text-xs" style={{ color: 'var(--text-muted)' }}>
                No logs to display
              </span>
            </div>
          ) : (
            <table className="w-full">
              <tbody>
                {filteredLogs.map((log, i) => (
                  <tr key={i} className={`border-b ${isDark ? 'border-slate-800 hover:bg-slate-800/50' : 'border-slate-100 hover:bg-slate-100'}`}>
                    <td className="px-3 py-1 whitespace-nowrap align-top" style={{ color: 'var(--text-muted)' }}>
                      {formatTime(log.timestamp)}
                    </td>
                    <td className="px-2 py-1 whitespace-nowrap align-top">
                      <span className={`inline-block text-[10px] font-bold px-1.5 py-0.5 rounded border ${LEVEL_BG[log.level] || LEVEL_BG.info}`}>
                        {log.level.toUpperCase()}
                      </span>
                    </td>
                    <td className="px-2 py-1 whitespace-nowrap align-top" style={{ color: 'var(--text-muted)' }}>
                      {log.service || '-'}
                    </td>
                    <td className={`px-3 py-1 align-top break-all ${LEVEL_COLORS[log.level] || 'text-slate-300'}`}>
                      {log.message}
                      {log.stack && <pre className="mt-1 text-[10px] text-red-400/70 whitespace-pre-wrap">{log.stack as string}</pre>}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      </div>
    </div>
  );
};

export default LogsPage;
